<template>
  <div :class="$attrs.class">
    <label 
      v-if="label" 
      class="form-label" 
      :for="id">
        {{ label }}:
    </label>

    <date-picker 
      v-model="dateSelected"
      :format="formatDateToPtBR"
      :enable-time-picker="false"
      :month-picker="isMonthPicker"
      :year-picker="isYearPicker"
      range
      locale="pt-BR"
      auto-apply
      @cleared="clearRangeEvent">
    </date-picker>
  </div>
</template>

<script>

import DatePicker from '@vuepic/vue-datepicker'
import format from 'date-fns/format'

import { 
  createDateFromObject,
  formatDate,
  formatDateFromDate,
  getLastDayFromMonth,
  createDateFromString,
  getDateFirstMonth,
  getDateLastMonth,
} from '../Utils/date'

export default {

  components: {
    DatePicker,
  },

  props: {
    modelValue: {
      type: Object,
    },

    label: {
      type: String,
      required: false,
    },

    intervalPicker: {
      type: Boolean,
      default: true,
      validator: (intervalPicker, props) => {
        return !(intervalPicker && props.yearPicker && props.monthPicker)
      }
    },

    monthPicker: {
      type: Boolean,
      default: false,
      validator: (monthPicker, props) => {
        return !(monthPicker && props.yearPicker && props.intervalPicker)
      }
    },

    yearPicker: {
      type: Boolean,
      default: false,
      validator: (yearPicker, props) => {
        return !(yearPicker && props.monthPicker && props.intervalPicker)
      }
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      dateSelected: this.handlerCreateDateFromProps(),
      alreadyUpdated: false,
    }
  },


  watch: {
    modelValue: function () {
      if (!this.alreadyUpdated)
        this.dateSelected = this.handlerCreateDateFromProps()
    },

    dateSelected: function () {
      this.dateSelectedEvent()
    },

    monthPicker: function () {
      this.clearRangeEvent()
    },

    yearPicker: function () {
      this.clearRangeEvent()
    },

    intervalPicker: function () {
      this.clearRangeEvent()
    }
  },

  computed: {
    isYearPicker() {
      return this.$props.yearPicker && (!this.$props.intervalPicker && !this.$props.monthPicker)
    },

    isMonthPicker() {
      return this.$props.monthPicker && (!this.$props.intervalPicker && !this.$props.yearPicker)
    },
  },

  methods: {
    clearRangeEvent() {
      this.emitEmptyData()
      this.dateSelected = ''
    },

    handlerCreateDateFromProps() {
      const { start, end } = this.$props.modelValue

      if (!start.length || !end.length)
        return

      else if (this.$props.yearPicker || this.$props.monthPicker)
        return this.setSelectedDateFromString(start, end)

      else if (this.$props.intervalPicker)
        return this.setSelectedDateRangePicker(start, end)
    },

    setSelectedDateRangePicker(dateStart, dateEnd) {
      return [
        createDateFromString(dateStart),
        createDateFromString(dateEnd)
      ]
    },

    setSelectedDateFromString(dateStart, dateEnd) {
      const start = createDateFromString(dateStart)
      const end = createDateFromString(dateEnd)

      return [
        {
          month: start.getMonth(),
          year: start.getFullYear(),
        },
        {
          month: end.getMonth(),
          year: end.getFullYear(),
        }
      ]
    },

    dateSelectedEvent: function () {

      if (this.dateSelected == null || !this.dateSelected.length)
        this.emitEmptyData()

      else if (this.$props.monthPicker)
        this.emitMonthData()

      else if (this.$props.yearPicker)
        this.emitYearData()

      else if (this.$props.intervalPicker)
        this.emitIntervalData()

      this.alreadyUpdated = true
      this.$nextTick().then(() => this.alreadyUpdated = false)
    },

    emitEmptyData() {
      this.$emit('update:modelValue', {
        start: '',
        end: '',
      })
    },

    emitMonthData() {
      const [dateStart, dateEnd] = this.dateSelected

      const firstMonthDate = createDateFromObject(dateStart)
      let lastMonthDate = createDateFromObject(dateEnd)

      lastMonthDate = getLastDayFromMonth(lastMonthDate)

      this.$emit('update:modelValue', {
        start: formatDate(firstMonthDate, 'yyyy-MM-dd'),
        end: formatDate(lastMonthDate, 'yyyy-MM-dd'),
      })
    },

    emitYearData() {
      const [dateStart, dateEnd] = this.dateSelected

      const firstMonthYearDate = getDateFirstMonth(dateStart)
      const lastMonthYearDate = getDateLastMonth(dateEnd)

      this.$emit('update:modelValue', {
        start: formatDate(firstMonthYearDate, 'yyyy-MM-dd'),
        end: formatDate(lastMonthYearDate, 'yyyy-MM-dd'),
      })
    },

    emitIntervalData() {
      const [dateStart, dateEnd] = this.dateSelected

      this.$emit('update:modelValue', {
        start: format(dateStart, 'yyyy-MM-dd'),
        end: format(dateEnd, 'yyyy-MM-dd')
      })
    },

    formatMonth([dateStart, dateEnd]) {
      return `${formatDate(dateStart, 'MMM/yyyy')} - ${formatDate(dateEnd, 'MMM/yyyy')}`
    },

    formatYear([dateStart, dateEnd]) {
      return `${formatDate(dateStart, 'yyyy')} - ${formatDate(dateEnd, 'yyyy')}`
    },

    formatFullDate([dateStart, dateEnd]) {
      const start = formatDateFromDate(dateStart, 'dd/MM/yyyy')
      const end = formatDateFromDate(dateEnd, 'dd/MM/yyyy')
      
      return `${start} - ${end}`
    },

    formatDateToPtBR(date) {
      if (this.$props.monthPicker)
        return this.formatMonth(date)

      else if (this.$props.yearPicker)
        return this.formatYear(date)

      else if (this.$props.intervalPicker)
        return this.formatFullDate(date)
    }
  }
}

</script>


<style scoped>

.dp__theme_dark,
.dp__theme_light {
  --dp-border-color: #383874;
  --dp-text-color: #383874;
  --dp-icon-color: #383874;
}

</style>
